<template>
  <div class="bg-white rounded-lg shadow-lg p-6">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-xl font-semibold">Configuración de Cámara</h2>
      <div class="space-x-2">
        <button
          @click="$emit('add-camera')"
          class="px-4 py-2 bg-[#18223B] text-white rounded-lg hover:bg-[#18223B]/90 transition-colors"
          :disabled="isAddingCamera"
        >
          {{ isAddingCamera ? 'Colocando cámara...' : 'Añadir Cámara' }}
        </button>
        <button
          @click="$emit('reset')"
          class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Reiniciar
        </button>
      </div>
    </div>

    <div class="space-y-4">
      <div>
        <div class="flex items-center mb-2">
          <label class="text-sm font-medium text-gray-700">
            Campo de Visión (FOV)
          </label>
          <button class="ml-2 text-gray-400 hover:text-gray-600" v-tooltip="'Ángulo de visión horizontal de la cámara'">
            <Info :size="16" />
          </button>
        </div>
        <input
          type="range"
          v-model.number="localSettings.fov"
          min="40"
          max="110"
          step="1"
          class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#18223B]"
        />
        <div class="mt-1 text-sm text-gray-500">{{ localSettings.fov }}°</div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Altura (m)
          </label>
          <input
            type="number"
            v-model.number="localSettings.height"
            min="0.5"
            max="400"
            step="0.5"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#18223B]"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Inclinación (°)
          </label>
          <input
            type="range"
            v-model.number="localSettings.tilt"
            min="-60"
            max="20"
            step="1"
            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#18223B]"
          />
          <div class="mt-1 text-sm text-gray-500">{{ localSettings.tilt }}°</div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Altura Objetivo (m)
          </label>
          <input
            type="number"
            v-model.number="localSettings.targetHeight"
            min="-100"
            max="300"
            step="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#18223B]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { Info } from 'lucide-vue-next';
import { CameraSettings } from '../types';

const props = defineProps<{
  settings: CameraSettings;
  isAddingCamera: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:settings', settings: CameraSettings): void;
  (e: 'add-camera'): void;
  (e: 'reset'): void;
}>();

const localSettings = ref<CameraSettings>({ ...props.settings });

watch(localSettings, (newSettings) => {
  emit('update:settings', newSettings);
}, { deep: true });

watch(() => props.settings, (newSettings) => {
  localSettings.value = { ...newSettings };
}, { deep: true });
</script>